import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LogOut, Bell, NotebookPen, X, Trash2, Check, Share2, Copy, RefreshCw, Printer, BookOpen, GraduationCap } from "lucide-react";
import QRCode from "qrcode";
import CalendarView from "../../components/CalendarView";
import TomorrowGlance from "../../components/TomorrowGlance";
import { useClock, fmtTime, ClockToggle } from "../../lib/clockFormat";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { useAuth } from "../../context/AuthContext";

const SUBJECT_COLORS = ["#FCA5A5", "#86EFAC", "#FDE047", "#D8B4FE", "#7DD3FC", "#A7E4D3", "#FDBA74", "#C7D2FE"];
const TYPE_STYLE = { break: "#FDE68A88", lunch: "#F9A8D488", free: "#C7D2FE88" };

export default function PupilHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [entries, setEntries] = useState({});
  const [notes, setNotes] = useState({});
  const [notices, setNotices] = useState([]);
  const [editingSlot, setEditingSlot] = useState(null);
  const [slotForm, setSlotForm] = useState({ subject_id: "", teacher_id: "", room_id: "" });
  const [showAllTeachers, setShowAllTeachers] = useState(false);
  const [noteDay, setNoteDay] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [week, setWeek] = useState("A");
  const h24 = useClock();
  const [view, setView] = useState("timetable");
  const [examForm, setExamForm] = useState({ title: "", date: "", start_time: "", notes: "" });
  const [qrDataUrl, setQrDataUrl] = useState(null);

  const load = async () => {
    try {
      const [boot, ns] = await Promise.all([api.get("/pupil/bootstrap"), api.get("/pupil/notices")]);
      setData(boot.data);
      setEntries(boot.data.pupil.timetable || {});
      setNotes(boot.data.pupil.notes || {});
      setShareToken(boot.data.pupil.share_token || null);
      setShareEnabled(!!boot.data.pupil.share_enabled);
      setNotices(ns.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const subjectColor = useMemo(() => {
    const map = {};
    (data?.subjects || []).forEach((s, i) => { map[s.id] = SUBJECT_COLORS[i % SUBJECT_COLORS.length]; });
    return map;
  }, [data]);

  useEffect(() => {
    if (shareEnabled && shareToken) {
      QRCode.toDataURL(`${window.location.origin}/t/${shareToken}`, { width: 220, margin: 1 })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(null));
    } else {
      setQrDataUrl(null);
    }
  }, [shareEnabled, shareToken]);

  if (!data) return <div className="min-h-screen flex items-center justify-center font-display font-black text-2xl animate-pulse" data-testid="pupil-loading">Loading your week…</div>;

  const { days, slots } = data.school.schedule;
  const weeks = data.school.schedule.weeks || 1;
  const slotKey = (day, idx) => (weeks === 2 ? `${week}:${day}:${idx}` : `${day}:${idx}`);
  const dayOverrides = data.school.schedule.day_overrides || {};
  const daySlotsList = days.map((d) => dayOverrides[d]?.slots || slots);
  const rowCount = Math.max(slots.length, ...daySlotsList.map((s) => s.length));
  const slotFor = (day, idx) => (dayOverrides[day]?.slots || slots)[idx];
  const unread = notices.filter((n) => !n.read).length;

  const findName = (list, id) => list.find((x) => x.id === id)?.name || "";

  const visibleTeachers = slotForm.subject_id && !showAllTeachers
    ? data.teachers.filter((t) => (t.subject_ids || []).includes(slotForm.subject_id))
    : data.teachers;

  const openSlot = (day, idx) => {
    setEditingSlot({ day, idx });
    setShowAllTeachers(false);
    const existing = entries[slotKey(day, idx)] || {};
    setSlotForm({ subject_id: existing.subject_id || "", teacher_id: existing.teacher_id || "", room_id: existing.room_id || "" });
  };

  const clearSlot = async () => {
    const key = slotKey(editingSlot.day, editingSlot.idx);
    const next = { ...entries };
    delete next[key];
    setEntries(next);
    setEditingSlot(null);
    try {
      await api.put("/pupil/timetable", { entries: next });
      toast.success("Slot cleared");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveSlot = async () => {
    const key = slotKey(editingSlot.day, editingSlot.idx);
    const next = { ...entries };
    if (slotForm.subject_id || slotForm.teacher_id || slotForm.room_id) next[key] = slotForm;
    else delete next[key];
    setEntries(next);
    setEditingSlot(null);
    try {
      await api.put("/pupil/timetable", { entries: next });
      toast.success("Timetable updated");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const enableShare = async (regen = false) => {
    try {
      const { data } = await api.post(regen ? "/pupil/share/regenerate" : "/pupil/share");
      setShareToken(data.share_token);
      setShareEnabled(true);
      toast.success(regen ? "New link created — the old one no longer works" : "Share link created");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const disableShare = async () => {
    try {
      await api.delete("/pupil/share");
      setShareEnabled(false);
      toast.success("Share link turned off");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addExam = async () => {
    if (!examForm.title.trim() || !examForm.date) return toast.error("Add an exam name and date");
    try {
      await api.post("/pupil/exams", examForm);
      toast.success("Exam added to your calendar");
      setExamForm({ title: "", date: "", start_time: "", notes: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteExam = async (id) => {
    try {
      await api.delete(`/pupil/exams/${id}`);
      toast.success("Exam removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openNote = (day) => {
    setNoteDay(day);
    setNoteText(notes[day] || "");
  };

  const saveNote = async () => {
    const next = { ...notes };
    if (noteText.trim()) next[noteDay] = noteText;
    else delete next[noteDay];
    setNotes(next);
    setNoteDay(null);
    try {
      await api.put("/pupil/notes", { notes: next });
      toast.success("Note saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openNotice = async (n) => {
    if (!n.read) {
      setNotices(notices.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      api.post(`/pupil/notices/${n.id}/read`).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen" data-testid="pupil-home">
      <header className="border-b-2 border-zinc-900 bg-[#A7E4D3] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 shrink-0 rounded-xl border-2 border-zinc-900 bg-white flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <Clock className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-lg leading-tight truncate" data-testid="pupil-greeting">Hi {user.name}!</div>
              <div className="text-xs font-bold text-zinc-700 truncate">{user.school_name} · {user.year_group}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 no-print">
            <ClockToggle className="nb-btn bg-white px-3 py-2 text-sm font-bold" />
            <button onClick={() => window.print()} data-testid="pupil-print-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <Printer className="w-4 h-4" /> <span className="hidden sm:inline">Print</span>
            </button>
            <button onClick={() => setShareOpen(true)} data-testid="pupil-share-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">Share</span>
            </button>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="pupil-logout-btn"
              className="nb-btn bg-white px-3 py-2 text-sm">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-12 gap-6 items-start">
        <section className="lg:col-span-9 min-w-0">
          <div className="flex gap-2 mb-4 no-print" data-testid="pupil-view-tabs">
            {[["timetable", "Timetable"], ["calendar", "Calendar"]].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`pupil-view-${v}`}
                className={`nb-btn px-5 py-2 text-sm ${view === v ? "bg-[#A7E4D3]" : "bg-white text-zinc-600"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="hidden print:block font-display font-black text-xl mb-3">{user.name} — {user.school_name}{weeks === 2 ? ` · Week ${week}` : ""}</div>
          {weeks === 2 && view === "timetable" && (
            <div className="flex gap-2 mb-4 no-print" data-testid="week-tabs">
              {["A", "B"].map((w) => (
                <button key={w} onClick={() => setWeek(w)} data-testid={`week-tab-${w}`}
                  className={`nb-btn px-5 py-2 text-sm ${week === w ? "bg-[#1E3A8A] text-white" : "bg-white text-zinc-600"}`}>
                  Week {w}
                </button>
              ))}
            </div>
          )}
          {view === "timetable" && (
            <TomorrowGlance schedule={data.school.schedule} entries={entries} notes={notes} homework={data.homework || []} exams={data.exams || []}
              resolveEntry={(e) => ({ title: findName(data.subjects, e.subject_id) || "Class", sub: [findName(data.teachers, e.teacher_id), findName(data.rooms, e.room_id)].filter(Boolean).join(" · ") })} />
          )}
          {view === "timetable" && (
          <div className="nb-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[720px]" data-testid="pupil-timetable">
                <thead>
                  <tr>
                    <th className="border-b-2 border-r-2 border-zinc-900 bg-zinc-900 text-white text-xs font-bold p-3 text-left w-28 sticky left-0">Time</th>
                    {days.map((d) => (
                      <th key={d} className="border-b-2 border-zinc-900 bg-zinc-900 text-white p-3">
                        <div className="font-bold text-sm">{d}</div>
                        <button onClick={() => openNote(d)} data-testid={`note-btn-${d.toLowerCase()}`}
                          className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg border-2 transition-colors duration-150 no-print ${notes[d] ? "bg-[#FDE68A] border-zinc-900 text-zinc-900" : "border-white/40 text-white/80 hover:bg-white/10"}`}>
                          <NotebookPen className="w-3 h-3" /> {notes[d] ? "Note" : "Add note"}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: rowCount }).map((_, idx) => {
                    const slot = slots[idx];
                    return (
                    <tr key={idx}>
                      <td className="border-r-2 border-b-2 border-zinc-900 bg-white p-3 sticky left-0 z-10">
                        <div className="font-bold text-sm">{slot ? slot.label : "—"}</div>
                        <div className="text-xs text-zinc-500 font-semibold">{slot ? `${fmtTime(slot.start, h24)}–${fmtTime(slot.end, h24)}` : ""}</div>
                      </td>
                      {days.map((day, di) => {
                        const dSlot = daySlotsList[di][idx];
                        if (!dSlot) {
                          return <td key={day} className="border-b-2 border-zinc-300 p-1.5 bg-zinc-100" data-testid={`slot-${day.toLowerCase()}-${idx}`} />;
                        }
                        if (dSlot.type !== "period") {
                          return (
                            <td key={day} className="border-b-2 border-zinc-300 p-2 text-center text-xs font-bold text-zinc-500"
                              style={{ backgroundColor: TYPE_STYLE[dSlot.type] }} data-testid={`slot-${day.toLowerCase()}-${idx}`}>
                              {dSlot.label}
                            </td>
                          );
                        }
                        const entry = entries[slotKey(day, idx)];
                        const color = entry?.subject_id ? subjectColor[entry.subject_id] || "#E4E4E7" : null;
                        return (
                          <td key={day} className="border-b-2 border-zinc-300 p-1.5">
                            <button onClick={() => openSlot(day, idx)} data-testid={`timetable-slot-${day.toLowerCase()}-${idx}`}
                              className={`w-full min-h-[64px] rounded-lg border-2 p-2 text-left transition-[transform,box-shadow] duration-150 hover:scale-[1.02] ${entry ? "border-zinc-900 shadow-[2px_2px_0px_0px_#18181B]" : "border-dashed border-zinc-300 bg-zinc-50 hover:border-zinc-900 hover:bg-white"}`}
                              style={color ? { backgroundColor: color } : {}}>
                              {dayOverrides[day] && <div className="text-[9px] font-black text-zinc-400 mb-0.5">{dSlot.label} · {fmtTime(dSlot.start, h24)}–{fmtTime(dSlot.end, h24)}</div>}
                            {entry ? (
                                <>
                                  <div className="font-display font-bold text-sm leading-tight">{findName(data.subjects, entry.subject_id) || "Class"}</div>
                                  <div className="text-[11px] font-semibold text-zinc-700 mt-0.5 leading-tight">
                                    {[findName(data.teachers, entry.teacher_id), findName(data.rooms, entry.room_id)].filter(Boolean).join(" · ")}
                                  </div>
                                </>
                              ) : (
                                <span className="text-xs font-bold text-zinc-400">+ Add</span>
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}
          {view === "calendar" && (
            <div className="grid lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2">
                <CalendarView holidays={data.school.holidays || []} homework={data.homework || []} exams={data.exams || []} weeks={weeks} />
              </div>
              <div className="nb-card p-5" data-testid="pupil-exams-card">
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><GraduationCap className="w-5 h-5" /> Exams</h3>
                <div className="mt-3 space-y-2">
                  {(data.exams || []).map((e) => (
                    <div key={e.id} className="rounded-xl border-2 border-zinc-900 p-2.5 bg-[#FDFBF7] flex items-start justify-between gap-2" data-testid={`pupil-exam-${e.id}`}>
                      <div className="min-w-0">
                        <div className="font-bold text-sm">{e.title}</div>
                        <div className="text-[11px] text-zinc-500 font-semibold">
                          {new Date(e.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}{e.start_time ? ` · ${e.start_time}` : ""}{e.owner_type === "school" ? ` · school${e.year_group && e.year_group !== "all" ? ` (${e.year_group})` : ""}` : ""}
                        </div>
                        {e.notes && <div className="text-xs text-zinc-500 mt-1">{e.notes}</div>}
                      </div>
                      {e.owner_type === "pupil" && (
                        <button onClick={() => deleteExam(e.id)} data-testid={`pupil-exam-delete-${e.id}`} className="p-1.5 shrink-0 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150"><Trash2 className="w-3.5 h-3.5 text-red-700" /></button>
                      )}
                    </div>
                  ))}
                  {(data.exams || []).length === 0 && <p className="text-sm text-zinc-400 font-semibold" data-testid="pupil-exams-empty">No exams yet — add yours below.</p>}
                </div>
                <div className="mt-4 pt-4 border-t-2 border-zinc-100 space-y-2">
                  <input className="nb-input" placeholder="Exam name, e.g. Maths mock" data-testid="pupil-exam-title" value={examForm.title} onChange={(e2) => setExamForm({ ...examForm, title: e2.target.value })} maxLength={100} />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="date" className="nb-input" data-testid="pupil-exam-date" value={examForm.date} onChange={(e2) => setExamForm({ ...examForm, date: e2.target.value })} />
                    <input type="time" className="nb-input" data-testid="pupil-exam-time" value={examForm.start_time} onChange={(e2) => setExamForm({ ...examForm, start_time: e2.target.value })} />
                  </div>
                  <input className="nb-input" placeholder="Notes (optional)" data-testid="pupil-exam-notes" value={examForm.notes} onChange={(e2) => setExamForm({ ...examForm, notes: e2.target.value })} maxLength={200} />
                  <button onClick={addExam} data-testid="pupil-exam-add" className="nb-btn w-full bg-[#C7D2FE] py-2 text-sm"><GraduationCap className="w-4 h-4" /> Add my exam</button>
                </div>
              </div>
            </div>
          )}
          <p className="text-xs text-zinc-500 font-semibold mt-3">Tap any period slot to pick a subject, teacher and room from your school's list.</p>
        </section>

        <aside className="lg:col-span-3 space-y-4">
          {(data.homework || []).length > 0 && (
            <div className="space-y-3" data-testid="pupil-homework-list">
              <h2 className="font-display font-bold text-lg flex items-center gap-2"><BookOpen className="w-5 h-5" /> Homework</h2>
              {data.homework.map((h) => {
                const overdue = h.due_date < new Date().toISOString().slice(0, 10);
                return (
                  <div key={h.id} className="nb-card p-4" data-testid={`homework-card-${h.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-display font-bold text-sm">{h.slot_label} · {h.day}</span>
                      <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-[11px] font-bold ${overdue ? "bg-red-200" : "bg-[#FDE68A]"}`}>
                        due {new Date(h.due_date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap">{h.text}</p>
                    <p className="text-[11px] text-zinc-400 font-semibold mt-2">{h.teacher_name}{h.sub_group ? ` · ${h.sub_group}` : ""}</p>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Bell className="w-5 h-5" /> Notices
              {unread > 0 && <span className="px-2 py-0.5 rounded-lg border-2 border-zinc-900 bg-red-500 text-white text-xs font-black" data-testid="unread-badge">{unread}</span>}
            </h2>
          </div>
          <div className="space-y-3" data-testid="pupil-notices-list">
            {notices.map((n) => (
              <details key={n.id} className={`nb-card overflow-hidden ${n.read ? "opacity-80" : ""}`} onToggle={(e) => e.target.open && openNotice(n)}>
                <summary className="p-4 cursor-pointer list-none" data-testid={`notice-item-${n.id}`}>
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-red-500 border border-zinc-900 mt-1.5 shrink-0" data-testid={`notice-unread-dot-${n.id}`} />}
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm leading-tight">{n.title}</div>
                      <div className="text-[11px] font-semibold text-zinc-400 mt-1">
                        {new Date(n.created_at).toLocaleDateString("en-GB")} · {n.audience === "all" ? "Whole school" : n.audience}
                      </div>
                    </div>
                  </div>
                </summary>
                <p className="px-4 pb-4 text-sm text-zinc-600 whitespace-pre-wrap border-t-2 border-zinc-100 pt-3">{n.body}</p>
              </details>
            ))}
            {notices.length === 0 && (
              <div className="nb-card p-6 text-center text-sm text-zinc-500" data-testid="pupil-notices-empty">No notices right now. Enjoy the quiet!</div>
            )}
          </div>
        </aside>
      </main>

      {editingSlot && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="slot-editor-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-xl">{editingSlot.day} · {slotFor(editingSlot.day, editingSlot.idx)?.label}</h3>
              <button onClick={() => setEditingSlot(null)} data-testid="slot-editor-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs font-semibold text-zinc-400 mb-5">{fmtTime(slotFor(editingSlot.day, editingSlot.idx)?.start, h24)}–{fmtTime(slotFor(editingSlot.day, editingSlot.idx)?.end, h24)}</p>
            <div className="space-y-4">
              <div>
                <label className="nb-label" htmlFor="slot-subject">Subject / class</label>
                <select id="slot-subject" className="nb-input mt-2" data-testid="slot-subject-select"
                  value={slotForm.subject_id} onChange={(e) => setSlotForm({ ...slotForm, subject_id: e.target.value })}>
                  <option value="">Choose…</option>
                  {data.subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="nb-label" htmlFor="slot-teacher">Teacher</label>
                <select id="slot-teacher" className="nb-input mt-2" data-testid="slot-teacher-select"
                  value={slotForm.teacher_id} onChange={(e) => setSlotForm({ ...slotForm, teacher_id: e.target.value })}>
                  <option value="">Choose…</option>
                  {visibleTeachers.map((t) => <option key={t.id} value={t.id}>{t.name}{t.detail ? ` (${t.detail})` : ""}</option>)}
                </select>
                {slotForm.subject_id && (
                  <>
                    {visibleTeachers.length === 0 && (
                      <p className="text-xs font-semibold text-zinc-400 mt-2">No teachers are linked to this subject yet — tick below to pick any staff member.</p>
                    )}
                    <label className="flex items-center gap-2 mt-2 text-xs font-bold text-zinc-500 cursor-pointer">
                      <input type="checkbox" data-testid="slot-show-all-teachers" checked={showAllTeachers}
                        onChange={(e) => setShowAllTeachers(e.target.checked)} className="w-4 h-4 accent-zinc-900" />
                      Show all staff (cover / substitute)
                    </label>
                  </>
                )}
              </div>
              <div>
                <label className="nb-label" htmlFor="slot-room">Room</label>
                <select id="slot-room" className="nb-input mt-2" data-testid="slot-room-select"
                  value={slotForm.room_id} onChange={(e) => setSlotForm({ ...slotForm, room_id: e.target.value })}>
                  <option value="">Choose…</option>
                  {data.rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={saveSlot} data-testid="slot-save-btn" className="nb-btn flex-1 bg-[#A7E4D3] py-2.5">
                  <Check className="w-4 h-4" /> Save
                </button>
                <button onClick={clearSlot} data-testid="slot-clear-btn" type="button"
                  className="nb-btn bg-red-100 px-4 py-2.5 text-red-700">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {shareOpen && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="share-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl flex items-center gap-2"><Share2 className="w-5 h-5" /> Share your timetable</h3>
              <button onClick={() => setShareOpen(false)} data-testid="share-modal-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-zinc-500 leading-relaxed">
              Get a private link — a long random address nobody can guess. Open it on your phone and "Add to Home Screen" to keep your timetable one tap away. It shows your timetable only, never your notes.
            </p>
            {shareEnabled && shareToken ? (
              <>
                <div className="mt-4 flex gap-2">
                  <input readOnly className="nb-input flex-1 text-xs font-mono" data-testid="share-link-input"
                    value={`${window.location.origin}/t/${shareToken}`}
                    onFocus={(e) => e.target.select()} />
                  <button data-testid="share-copy-btn"
                    onClick={async () => {
                      const ok = await copyToClipboard(`${window.location.origin}/t/${shareToken}`);
                      ok ? toast.success("Link copied!") : toast.error("Couldn't copy — long-press the link to copy it manually");
                    }}
                    className="nb-btn bg-[#A7E4D3] px-3 py-2 text-sm shrink-0">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                {qrDataUrl && (
                  <div className="mt-4 text-center">
                    <img src={qrDataUrl} alt="QR code for your timetable link" data-testid="share-qr-code"
                      className="mx-auto rounded-xl border-2 border-zinc-900 shadow-[3px_3px_0px_0px_#18181B]" />
                    <p className="text-xs font-bold text-zinc-500 mt-2">Scan with a phone camera to open it instantly</p>
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <button onClick={() => enableShare(true)} data-testid="share-regenerate-btn" className="nb-btn flex-1 bg-[#FDE68A] py-2 text-sm">
                    <RefreshCw className="w-4 h-4" /> New link
                  </button>
                  <button onClick={disableShare} data-testid="share-disable-btn" className="nb-btn flex-1 bg-red-100 text-red-700 py-2 text-sm">
                    <Trash2 className="w-4 h-4" /> Turn off
                  </button>
                </div>
              </>
            ) : (
              <button onClick={() => enableShare(false)} data-testid="share-create-btn" className="nb-btn w-full bg-[#A7E4D3] py-2.5 mt-4">
                <Share2 className="w-4 h-4" /> Create my private link
              </button>
            )}
          </div>
        </div>
      )}

      {noteDay && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="note-editor-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-xl flex items-center gap-2"><NotebookPen className="w-5 h-5" /> {noteDay} note</h3>
              <button onClick={() => setNoteDay(null)} data-testid="note-editor-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea className="nb-input min-h-[140px] resize-y" data-testid="note-textarea" maxLength={2000}
              placeholder="Homework, kit to bring, reminders…" value={noteText} onChange={(e) => setNoteText(e.target.value)} />
            <button onClick={saveNote} data-testid="note-save-btn" className="nb-btn w-full bg-[#FDE68A] py-2.5 mt-4">
              <Check className="w-4 h-4" /> Save note
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
